import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Link2, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Panel } from "@/components/app/common";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

function PickRevision({ order, onClose, onLinked }) {
  const { data } = useFetch(`/design-revisions/approved?customer_id=${order.customer_id}`);
  const link = async (r) => {
    try {
      await api.post(`/orders/${order.id}/design-link`, { design_revision_id: r.id });
      toast.success(`Linked ${r.design_no} R${r.revision_no}`);
      onLinked();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-2 max-h-80 overflow-y-auto">
      {(data || []).map((r) => (
        <button key={r.id} onClick={() => link(r)} data-testid={`pick-revision-${r.id}`}
          className="w-full text-left border border-slate-200 rounded p-3 hover:border-sky-400 hover:bg-sky-50/50">
          <div className="text-sm font-medium"><span className="font-mono">{r.design_no} · R{r.revision_no}</span> — {r.title}</div>
          <div className="text-xs text-slate-500">Approved {fmtDate(r.approved_at)} {r.is_latest && "· latest"}</div>
        </button>
      ))}
      {data && data.length === 0 && <p className="text-sm text-slate-500">No approved design revisions for this customer yet.</p>}
    </div>
  );
}

export function DesignLinkCard({ order, reload }) {
  const { can } = useMe();
  const [open, setOpen] = useState(false);
  const s = order.design_snapshot;
  const closed = ["closed", "cancelled"].includes(order.status);
  const unlink = async () => {
    if (!window.confirm("Remove design link from this order?")) return;
    try { await api.delete(`/orders/${order.id}/design-link`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Design reference" testid="design-link-card"
      actions={can("write") && !closed && <>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="link-design-btn"><Link2 className="h-4 w-4 mr-1" />{s ? "Change" : "Link design"}</Button>
        {s && <Button size="sm" variant="ghost" onClick={unlink} data-testid="unlink-design-btn"><Unlink className="h-4 w-4" /></Button>}
      </>}>
      {s ? (
        <div className="text-sm">
          <Link to={`/designs/${order.design_id}`} className="font-mono text-sky-700 hover:underline" data-testid="linked-design-ref">{s.design_no} · R{s.revision_no}</Link>
          <div className="text-slate-600">{s.title}</div>
          <div className="text-xs text-slate-500">Approved {fmtDate(s.approved_at)} · pinned to this revision</div>
          {order.design_newer_revision != null && (
            <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex gap-2" data-testid="design-newer-warning">
              <AlertTriangle className="h-4 w-4 shrink-0" /> A newer approved revision R{order.design_newer_revision} exists. This order still uses R{s.revision_no}.
            </div>
          )}
        </div>
      ) : <p className="text-sm text-slate-500">No design linked. Link an approved revision when available.</p>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="link-design-dialog">
          <DialogHeader><DialogTitle>Link approved design revision</DialogTitle></DialogHeader>
          {open && <PickRevision order={order} onClose={() => setOpen(false)} onLinked={reload} />}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
